/**
 * DriverSettingsPage — /driver/settings
 * Driver self-service: editable contact/CDL info, view assigned equipment, compliance warnings
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import api, { driverApi, type FullDriverProfile } from '../lib/api'
import { normalizeZipInput, useZipLocation } from '../lib/useZipLocation'

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']
const ENDORSEMENTS = ['H','N','P','T','X','S','L']
const RESTRICTIONS = ['B','E','K','L','M','N','O','V','W']

const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500'
const labelCls = 'block text-xs font-medium text-gray-600 mb-1'

function TagPicker({ label, options, value, onChange }: {
  label: string; options: string[]; value: string[]; onChange: (v: string[]) => void
}) {
  return (
    <div>
      <div className={labelCls}>{label}</div>
      <div className="flex gap-1 flex-wrap">
        {options.map(tag => (
          <button key={tag} type="button"
            onClick={() => onChange(value.includes(tag) ? value.filter(t => t !== tag) : [...value, tag])}
            className={`px-2.5 py-1 rounded text-xs font-semibold border transition-colors
              ${value.includes(tag) ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
            {tag}
          </button>
        ))}
      </div>
    </div>
  )
}

function EquipUnitCard({ label, unit }: { label: string; unit: any }) {
  if (!unit) return (
    <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-4 text-center text-sm text-gray-400">
      {label}: Not assigned — contact your dispatcher
    </div>
  )
  const now = new Date()
  const expiredInsp = unit.inspectionExpires && new Date(unit.inspectionExpires) < now
  const expiredIns  = unit.insuranceExpires  && new Date(unit.insuranceExpires)  < now
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{label}</div>
      <div className="font-medium text-gray-900">{unit.displayName ?? unit.unitNumber ?? 'Assigned unit'}</div>
      <div className="text-sm text-gray-600">{[unit.year, unit.make, unit.model].filter(Boolean).join(' ')}</div>
      {unit.plateNumber && <div className="text-xs text-gray-400 mt-1">Plate: {unit.plateNumber} ({unit.plateState})</div>}
      {expiredInsp && <div className="text-xs text-red-700 font-medium mt-1">⚠ Inspection expired {unit.inspectionExpires}</div>}
      {expiredIns  && <div className="text-xs text-red-700 font-medium mt-0.5">⚠ Insurance expired {unit.insuranceExpires}</div>}
    </div>
  )
}

export default function DriverSettingsPage() {
  const qc = useQueryClient()

  const meQ = useQuery({
    queryKey: ['driver-me'],
    queryFn: () => driverApi.getMe().then(r => r.data),
  })

  const profile = meQ.data?.profile
  const [form, setForm] = useState<Partial<FullDriverProfile>>({})
  const [profileMsg, setProfileMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [reqForm, setReqForm] = useState({ tractorDescription: '', trailerDescription: '', notes: '' })
  const [reqMsg, setReqMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const zipLookup = useZipLocation({
    zip: String(form.zip ?? ''),
    onCity: (city) => setForm(prev => ({ ...prev, city })),
    onState: (state) => setForm(prev => ({ ...prev, state })),
    lookupZip: api.geo.zipLookup,
  })

  useEffect(() => {
    if (!profile) return
    setForm({
      firstName:             profile.firstName ?? '',
      middleName:            profile.middleName ?? '',
      lastName:              profile.lastName ?? '',
      phone:                 profile.phone ?? '',
      email:                 profile.email ?? '',
      addressLine1:          profile.addressLine1 ?? '',
      addressLine2:          profile.addressLine2 ?? '',
      city:                  profile.city ?? '',
      state:                 profile.state ?? '',
      zip:                   profile.zip ?? '',
      emergencyContactName:  profile.emergencyContactName ?? '',
      emergencyContactPhone: profile.emergencyContactPhone ?? '',
      cdlClass:              profile.cdlClass ?? '',
      licenseNumber:         profile.licenseNumber ?? '',
      licenseState:          profile.licenseState ?? '',
      licenseExpires:        profile.licenseExpires ?? '',
      medicalCertExpires:    profile.medicalCertExpires ?? '',
      endorsements:          profile.endorsements ?? [],
      restrictions:          profile.restrictions ?? [],
      operatingStates:       profile.operatingStates ?? [],
      preferredLanes:        profile.preferredLanes ?? [],
    })
  }, [profile])

  const f = (k: keyof FullDriverProfile) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [k]: e.target.value }))

  const profileMut = useMutation({
    mutationFn: () => driverApi.updateMe(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['driver-me'] })
      setProfileMsg({ ok: true, text: 'Settings saved.' })
      setTimeout(() => setProfileMsg(null), 3000)
    },
    onError: (err: any) => setProfileMsg({ ok: false, text: err?.response?.data?.error ?? err.message }),
  })

  const reqMut = useMutation({
    mutationFn: () => driverApi.requestEquipment(reqForm),
    onSuccess: (r) => {
      setReqForm({ tractorDescription: '', trailerDescription: '', notes: '' })
      setReqMsg({ ok: true, text: r.data.message })
      setTimeout(() => setReqMsg(null), 6000)
    },
    onError: (err: any) => setReqMsg({ ok: false, text: err?.response?.data?.error ?? err.message }),
  })

  if (meQ.isLoading) return <div className="p-6 text-gray-400 text-sm">Loading your settings…</div>

  const warnings = profile?.complianceWarnings ?? []
  const errors   = warnings.filter(w => w.includes('_EXPIRED') || w.includes('FAILED'))
  const cautions = warnings.filter(w => !errors.includes(w))

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Driver Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Update your contact info, CDL details, and preferred lanes.</p>
      </div>

      {/* Compliance summary */}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-1">
          <div className="text-sm font-semibold text-amber-900 mb-2">Compliance Notices</div>
          {errors.map((w, i) => <div key={i} className="text-xs text-red-700 font-medium">✗ {w}</div>)}
          {cautions.map((w, i) => <div key={i} className="text-xs text-amber-800">⚠ {w}</div>)}
          <p className="text-xs text-amber-700 mt-2">Contact your dispatcher to resolve compliance issues.</p>
        </div>
      )}

      {/* Assigned equipment (read-only) */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900">Assigned Equipment</h2>
        <div className="grid grid-cols-2 gap-3">
          <EquipUnitCard label="Tractor" unit={meQ.data?.tractorUnit} />
          <EquipUnitCard label="Trailer" unit={meQ.data?.trailerUnit} />
        </div>
        {(!meQ.data?.tractorUnit || !meQ.data?.trailerUnit) && (
          <p className="text-xs text-gray-500">
            Need different equipment? Use the request form below to notify your dispatcher.
          </p>
        )}
      </section>

      {/* Editable settings */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Personal Info</h2>
        {profileMsg && (
          <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${profileMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {profileMsg.text}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>First Name</label>
            <input value={(form.firstName as string) ?? ''} onChange={f('firstName')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Last Name</label>
            <input value={(form.lastName as string) ?? ''} onChange={f('lastName')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input value={(form.phone as string) ?? ''} onChange={f('phone')} placeholder="+1 555-0100" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input value={(form.email as string) ?? ''} onChange={f('email')} className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Address</label>
            <input value={(form.addressLine1 as string) ?? ''} onChange={f('addressLine1')} placeholder="Street address" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>City</label>
            <input value={(form.city as string) ?? ''} onChange={f('city')} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>State</label>
              <select value={(form.state as string) ?? ''} onChange={f('state')} className={inputCls}>
                <option value="">—</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>ZIP</label>
              <input value={(form.zip as string) ?? ''} onChange={(e) => setForm(prev => ({ ...prev, zip: normalizeZipInput(e.target.value) }))} maxLength={10} inputMode="numeric" className={inputCls} />
              {(zipLookup.loading || zipLookup.message) && <p className="mt-1 text-xs text-gray-500" role="status">{zipLookup.loading ? 'Looking up ZIP...' : zipLookup.message}</p>}
            </div>
          </div>
          <div>
            <label className={labelCls}>Emergency Contact</label>
            <input value={(form.emergencyContactName as string) ?? ''} onChange={f('emergencyContactName')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Emergency Phone</label>
            <input value={(form.emergencyContactPhone as string) ?? ''} onChange={f('emergencyContactPhone')} className={inputCls} />
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700">CDL Info</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>CDL Class</label>
              <select value={(form.cdlClass as string) ?? ''} onChange={f('cdlClass')} className={inputCls}>
                <option value="">Not set</option>
                <option value="A">CDL-A</option>
                <option value="B">CDL-B</option>
                <option value="C">CDL-C</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>License State</label>
              <select value={(form.licenseState as string) ?? ''} onChange={f('licenseState')} className={inputCls}>
                <option value="">—</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>License Number</label>
              <input value={(form.licenseNumber as string) ?? ''} onChange={f('licenseNumber')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>License Expires</label>
              <input type="date" value={(form.licenseExpires as string) ?? ''} onChange={f('licenseExpires')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Medical Card Expires</label>
              <input type="date" value={(form.medicalCertExpires as string) ?? ''} onChange={f('medicalCertExpires')} className={inputCls} />
            </div>
          </div>
          <TagPicker label="Endorsements" options={ENDORSEMENTS}
            value={(form.endorsements as string[]) ?? []}
            onChange={vals => setForm(p => ({ ...p, endorsements: vals }))} />
          <TagPicker label="Restrictions" options={RESTRICTIONS}
            value={(form.restrictions as string[]) ?? []}
            onChange={vals => setForm(p => ({ ...p, restrictions: vals }))} />
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Preferences</h3>
          <div>
            <label className={labelCls}>Preferred Lanes (comma-separated)</label>
            <input
              value={((form.preferredLanes as string[]) ?? []).join(', ')}
              onChange={e => setForm(p => ({
                ...p, preferredLanes: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
              }))}
              placeholder="TX→CA, TX→FL"
              className={inputCls} />
          </div>
        </div>

        <button onClick={() => profileMut.mutate()} disabled={profileMut.isPending}
          className="mt-5 bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-800 disabled:opacity-50">
          {profileMut.isPending ? 'Saving…' : 'Save Settings'}
        </button>
      </section>

      {/* Equipment request form */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Request Equipment Change</h2>
        <p className="text-sm text-gray-500 mb-4">Need a different tractor or trailer? Submit a request — your dispatcher will review and assign.</p>
        {reqMsg && (
          <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${reqMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {reqMsg.text}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Tractor Request (unit number or description)</label>
            <input value={reqForm.tractorDescription} onChange={e => setReqForm(p => ({ ...p, tractorDescription: e.target.value }))}
              placeholder="e.g. T-12 or any available 600hp" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Trailer Request</label>
            <input value={reqForm.trailerDescription} onChange={e => setReqForm(p => ({ ...p, trailerDescription: e.target.value }))}
              placeholder="e.g. TR-07 (Cottrell)" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={reqForm.notes} onChange={e => setReqForm(p => ({ ...p, notes: e.target.value }))}
              rows={2} placeholder="Reason for request or specific dates needed"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          </div>
          <button onClick={() => reqMut.mutate()} disabled={reqMut.isPending || (!reqForm.tractorDescription && !reqForm.trailerDescription)}
            className="bg-gray-800 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-gray-900 disabled:opacity-50">
            {reqMut.isPending ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </section>
    </div>
  )
}
